import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { signOut } from '../api/auth';
import { useApp } from '../state/AppContext';

/* ---------------------------------------------------------------------------
   Navigation is filtered by the signed-in user's capabilities, which come from
   the server-side Role x Resource x Permission matrix. A screen can also be
   present-but-read-only for a role — Incident & Rewards for an Executive —
   which is why visibility keys off the "view" permission and the entry form
   keys off "edit".
   --------------------------------------------------------------------------- */

interface NavEntry {
  to: string;
  label: string;
  resource: string;
  /** Any of these on the resource shows the entry. Defaults to view. */
  permissions?: string[];
}

const NAV: NavEntry[] = [
  { to: '/daily', label: 'Daily Summary', resource: 'dailySummary' },
  { to: '/incidents', label: 'Incident & Rewards', resource: 'incidents' },
  { to: '/punches', label: 'Punch Data', resource: 'dailySummary' },
  { to: '/import', label: 'Data Import', resource: 'dataImport' },
  { to: '/dashboard', label: 'Executive Dashboard', resource: 'dashboard' },
  { to: '/scorecard', label: 'Scorecard Summary', resource: 'scorecard', permissions: ['view', 'viewSelf'] },
  { to: '/employees', label: 'Employees', resource: 'employees' },
  { to: '/schedules', label: 'Employee Schedules', resource: 'schedules' },
  { to: '/settings', label: 'Settings', resource: 'settings' },
  { to: '/admin', label: 'Administration', resource: 'users' },
];

export function Shell() {
  const { me, can, devUser, switchDevUser, reference } = useApp();
  const visible = NAV
    .filter((n) => (n.permissions ?? ['view']).some((p) => can(n.resource, p)))
    // Someone who can only see their own scorecard goes straight to it.
    .map((n) =>
      n.resource === 'scorecard' && !can('scorecard', 'view') && me.employeeId != null
        ? { ...n, to: `/employee/${me.employeeId}`, label: 'My Scorecard' }
        : n);

  const reached = Object.entries(me.scopes)
    .filter(([key]) => !key.endsWith(':viewSelf'))
    .map(([, codes]) => codes);
  const scopeLabel =
    reached.length === 0 ? 'Own scorecard'
    : reached.some((codes) => codes === null) ? 'All locations'
    : [...new Set(reached.flatMap((codes) => codes ?? []))]
        .map((code) => reference.facilities.find((f) => f.code === code)?.name ?? code)
        .join(', ');

  // Below 1024px the sidebar is an off-canvas drawer rather than a column, so
  // it needs an open state — and one that closes itself the moment the drawer
  // stops being useful: on navigation, on Escape, and on a click outside.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    // The page underneath must not scroll while the drawer covers it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <div className="app">
      {/* Mobile only — CSS hides this entirely at desktop widths, where the
          sidebar is already on screen and needs no opening. */}
      <header className="topbar">
        <button
          type="button"
          className="topbar__menu"
          aria-label="Open navigation menu"
          aria-expanded={menuOpen}
          aria-controls="app-sidebar"
          onClick={() => setMenuOpen(true)}
        >
          <span className="hamburger" aria-hidden="true">
            <span /><span /><span />
          </span>
        </button>
        <div className="topbar__wordmark">CYNTOX</div>
      </header>

      {menuOpen && <div className="sidebar-scrim" onClick={() => setMenuOpen(false)} />}

      <nav id="app-sidebar" className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}>
        <button
          type="button"
          className="sidebar__close"
          aria-label="Close navigation menu"
          onClick={() => setMenuOpen(false)}
        >
          ×
        </button>
        <div className="sidebar__brand">
          <div className="sidebar__wordmark">CYNTOX</div>
          <div className="sidebar__tagline">BIOHAZARD SOLUTIONS</div>
          <div className="sidebar__badge">FACILITY SCORECARD</div>
        </div>

        {/* Development only. In production the role comes from Entra ID and
            there is no switcher at all. */}
        {me.devMode && (
          <>
            <div style={{ padding: '0 20px 6px', fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)' }}>
              VIEW AS <span style={{ color: 'var(--orange)' }}>· DEV ONLY</span>
            </div>
            <div style={{ padding: '0 20px 16px' }}>
              <select
                className="control"
                style={{ width: '100%' }}
                value={devUser ?? me.upn}
                onChange={(e) => switchDevUser(e.target.value)}
                aria-label="Simulate a role"
              >
                {(me.devUsers ?? []).map((u) => (
                  <option key={u.upn} value={u.upn}>{u.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="sidebar__nav">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item${isActive ? ' nav-item--active' : ''}`}
            >
              <span className="nav-item__dot" />
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="sidebar__footer">
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, color: 'var(--muted)', marginBottom: 4 }}>
            SIGNED IN AS
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>{me.displayName}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{me.roleSummary} · {scopeLabel}</div>
          {/* Only where there is a real session to end. In development the
              switcher above is how you change who you are, and signing out of
              a dev header would mean nothing. */}
          {!me.devMode && (
            <button
              className="btn btn--sm"
              style={{ width: '100%', marginTop: 10 }}
              onClick={() => { void signOut(); }}
            >
              Sign out
            </button>
          )}
          <div className="sidebar__version">
            <span>Version: {__APP_VERSION__}</span>
            <button
              type="button"
              className="sidebar__refresh"
              aria-label="Reload the page"
              title="Reload the page"
              onClick={() => { window.location.reload(); }}
            >
              <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
                <path
                  d="M13.2 8A5.2 5.2 0 1 1 11.6 4.2"
                  fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
                />
                <path
                  d="M13.4 1.9v3.4H10"
                  fill="none" stroke="currentColor" strokeWidth="1.7"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
